import { Controller } from '@hotwired/stimulus'
import L from 'leaflet'
import 'leaflet.heat'

export default class extends Controller {
  static values = { zipCounts: Object, centerZip: String }

  async connect() {
    const centroids = await this.loadCentroids()
    this.initMap(centroids)
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  async loadCentroids() {
    const response = await fetch('/zip_centroids.json')
    return response.json()
  }

  initMap(centroids) {
    const defaultCenter = [39.8, -98.6]
    const defaultZoom = 4

    this.map = L.map(this.element, {
      center: defaultCenter,
      zoom: defaultZoom,
      scrollWheelZoom: true
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18
    }).addTo(this.map)

    const heatPoints = []
    const zipCounts = this.zipCountsValue
    const maxCount = Math.max(...Object.values(zipCounts), 1)

    const zipPoints = []

    for (const [zip, count] of Object.entries(zipCounts)) {
      const coords = centroids[zip]
      if (coords) {
        const intensity = count / maxCount
        heatPoints.push([coords[0], coords[1], intensity])
        zipPoints.push({ zip, count, lat: coords[0], lng: coords[1] })
      }
    }

    if (heatPoints.length > 0) {
      L.heatLayer(heatPoints, {
        radius: 40,
        blur: 25,
        maxZoom: 17,
        minOpacity: 0.4,
        max: 1.0,
        gradient: {
          0.2: '#313695',
          0.4: '#4575b4',
          0.5: '#74add1',
          0.6: '#fee090',
          0.7: '#f46d43',
          0.8: '#d73027',
          1.0: '#a50026'
        }
      }).addTo(this.map)
    }

    this.setupHoverTooltip(zipPoints)
  }

  setupHoverTooltip(zipPoints) {
    const tooltip = L.tooltip({ direction: 'top', offset: [0, -10] })
    let tooltipVisible = false

    this.map.on('mousemove', (e) => {
      const heatRadiusPx = 40
      const nearby = []

      for (const zp of zipPoints) {
        const pointPx = this.map.latLngToContainerPoint([zp.lat, zp.lng])
        const mousePx = this.map.latLngToContainerPoint(e.latlng)
        const dist = pointPx.distanceTo(mousePx)

        if (dist <= heatRadiusPx) {
          nearby.push(zp)
        }
      }

      if (nearby.length > 0) {
        const totalCount = nearby.reduce((sum, zp) => sum + zp.count, 0)
        const lines = nearby
          .sort((a, b) => b.count - a.count)
          .slice(0, 5)
          .map(zp => `${zp.zip}: ${zp.count}`)

        let html = `<strong>${totalCount} reservation${totalCount === 1 ? '' : 's'}</strong>`
        if (nearby.length > 1) {
          html += `<br><span style="font-size:0.85em">${lines.join('<br>')}</span>`
          if (nearby.length > 5) {
            html += `<br><span style="font-size:0.8em; color:#888">+${nearby.length - 5} more</span>`
          }
        } else {
          html += `<br><span style="font-size:0.85em">Zip: ${nearby[0].zip}</span>`
        }

        tooltip.setLatLng(e.latlng).setContent(html)
        if (!tooltipVisible) {
          tooltip.addTo(this.map)
          tooltipVisible = true
        }
      } else if (tooltipVisible) {
        tooltip.remove()
        tooltipVisible = false
      }
    })

    this.map.on('mouseout', () => {
      if (tooltipVisible) {
        tooltip.remove()
        tooltipVisible = false
      }
    })
  }
}
